import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GET_PROPERTY_NOTES, CREATE_NOTE, UPDATE_NOTE, DELETE_NOTE, PIN_NOTE } from '../../graphql/notes';
import { usePermissions } from '../../shared/hooks/usePermissions';
import { useAuth } from '../../shared/contexts/auth-context';

const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
        return new Date(dateString).toLocaleString('en-GB', {
            day: 'numeric', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    } catch {
        return '';
    }
};

const PropertyNotesTab = () => {
    const { id: propertyId } = useParams();
    const permissions = usePermissions();
    const { user } = useAuth();
    const currentUserId = user?.user?.id || user?.user?._id || user?.id;

    const [newContent, setNewContent] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editContent, setEditContent] = useState('');

    const { loading, error, data, refetch } = useQuery(GET_PROPERTY_NOTES, {
        variables: { propertyId },
        fetchPolicy: 'network-only'
    });

    const [createNote, { loading: creating }] = useMutation(CREATE_NOTE, {
        onCompleted: () => { setNewContent(''); refetch(); },
        onError: err => alert('Error creating note: ' + err.message)
    });

    const [updateNote, { loading: updating }] = useMutation(UPDATE_NOTE, {
        onCompleted: () => { setEditingId(null); setEditContent(''); refetch(); },
        onError: err => alert('Error updating note: ' + err.message)
    });

    const [deleteNote] = useMutation(DELETE_NOTE, {
        onCompleted: () => refetch(),
        onError: err => alert('Error deleting note: ' + err.message)
    });

    const [pinNote] = useMutation(PIN_NOTE, {
        onCompleted: () => refetch(),
        onError: err => alert('Error pinning note: ' + err.message)
    });

    const handleCreate = (e) => {
        e.preventDefault();
        if (!newContent.trim()) return;
        createNote({ variables: { propertyId, content: newContent } });
    };

    const handleEdit = (note) => {
        setEditingId(note.id);
        setEditContent(note.content);
    };

    const handleUpdate = (e, noteId) => {
        e.preventDefault();
        if (!editContent.trim()) return;
        updateNote({ variables: { noteId, content: editContent } });
    };

    const handleDelete = (noteId) => {
        if (window.confirm('Delete this note?')) {
            deleteNote({ variables: { noteId } });
        }
    };

    const canEditNote = (note) => {
        if (permissions.canManageAllNotes) return true;
        return note.authorId === currentUserId?.toString();
    };

    if (loading) return (
        <div className="flex justify-center items-center h-32">
            <div className="animate-spin inline-block w-6 h-6 border-4 border-blue-600 rounded-full border-t-transparent" />
        </div>
    );

    if (error) return (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            Failed to load notes: {error.message}
        </div>
    );

    const notes = data?.propertyNotes || [];

    return (
        <div className="space-y-4">
            {/* Add note form */}
            <div className="bg-white shadow sm:rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Add a Note</h3>
                <form onSubmit={handleCreate}>
                    <textarea
                        value={newContent}
                        onChange={e => setNewContent(e.target.value)}
                        placeholder="Write a note about this property..."
                        rows={3}
                        className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500 resize-none"
                    />
                    <div className="mt-2 flex justify-end">
                        <button
                            type="submit"
                            disabled={!newContent.trim() || creating}
                            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                        >
                            {creating ? 'Saving...' : 'Add Note'}
                        </button>
                    </div>
                </form>
            </div>

            {/* Notes list */}
            {notes.length === 0 ? (
                <div className="bg-white shadow sm:rounded-lg p-8 text-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <p className="text-sm text-gray-500">No notes yet. Be the first to add one.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {notes.map(note => (
                        <div key={note.id} className={`bg-white shadow sm:rounded-lg p-4 ${note.isPinned ? 'ring-2 ring-blue-400' : ''}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex items-center gap-2 min-w-0">
                                    {note.isPinned && (
                                        <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                                            Pinned
                                        </span>
                                    )}
                                    <div className="min-w-0">
                                        <span className="text-sm font-medium text-gray-900">{note.authorName}</span>
                                        <span className="ml-2 text-xs text-gray-400">{note.authorRole}</span>
                                        <span className="ml-2 text-xs text-gray-400">{formatDate(note.createdAt)}</span>
                                        {note.updatedAt && note.updatedAt !== note.createdAt && (
                                            <span className="ml-1 text-xs text-gray-400">(edited)</span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex-shrink-0 flex items-center gap-1">
                                    {permissions.canManageAllNotes && (
                                        <button
                                            onClick={() => pinNote({ variables: { noteId: note.id, isPinned: !note.isPinned } })}
                                            title={note.isPinned ? 'Unpin' : 'Pin'}
                                            className="p-1 text-gray-400 hover:text-blue-600 rounded"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill={note.isPinned ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                                            </svg>
                                        </button>
                                    )}
                                    {canEditNote(note) && (
                                        <>
                                            <button
                                                onClick={() => handleEdit(note)}
                                                className="p-1 text-gray-400 hover:text-blue-600 rounded"
                                                title="Edit"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => handleDelete(note.id)}
                                                className="p-1 text-gray-400 hover:text-red-600 rounded"
                                                title="Delete"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>

                            {editingId === note.id ? (
                                <form onSubmit={e => handleUpdate(e, note.id)} className="mt-3">
                                    <textarea
                                        value={editContent}
                                        onChange={e => setEditContent(e.target.value)}
                                        rows={3}
                                        className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500 resize-none"
                                        autoFocus
                                    />
                                    <div className="mt-2 flex gap-2 justify-end">
                                        <button
                                            type="button"
                                            onClick={() => { setEditingId(null); setEditContent(''); }}
                                            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={!editContent.trim() || updating}
                                            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                                        >
                                            {updating ? 'Saving...' : 'Save'}
                                        </button>
                                    </div>
                                </form>
                            ) : (
                                <p className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">{note.content}</p>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default PropertyNotesTab;
